import { useState } from 'react';
import type { MessageAttachment } from '@teo/widget-shared';
import { IconPlay } from '../components/icons.js';
import { useWidget } from '../context/WidgetContext.js';
import { useRichMedia } from './RichMediaContext.js';

type VideoAttachmentProps = {
  attachment: MessageAttachment;
  onMediaLoad?: () => void;
};

export function VideoAttachment({ attachment, onMediaLoad }: VideoAttachmentProps) {
  const { t } = useWidget();
  const { openLightbox } = useRichMedia();
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  if (failed) {
    return (
      <a
        className="teo-rich-media__fallback"
        href={attachment.url}
        target="_blank"
        rel="noopener noreferrer"
      >
        <span className="teo-rich-media__fallback-label">{t.mediaLoadError}</span>
        <span className="teo-rich-media__fallback-action">{t.mediaOpenPreview}</span>
      </a>
    );
  }

  return (
    <button
      type="button"
      className={[
        'teo-rich-media__thumb-btn',
        'teo-rich-media__thumb-btn--video',
        loaded ? '' : 'teo-media-skeleton',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={() =>
        openLightbox({
          kind: 'video',
          url: attachment.url,
          title: attachment.fileName,
        })
      }
      aria-label={attachment.fileName ?? 'Video'}
    >
      <span className="teo-rich-media__play">
        <IconPlay size={22} />
      </span>
      <video
        className="teo-rich-media__video-poster"
        src={attachment.url}
        muted
        playsInline
        preload="metadata"
        aria-hidden
        onLoadedMetadata={() => {
          setLoaded(true);
          onMediaLoad?.();
        }}
        onError={() => setFailed(true)}
      />
    </button>
  );
}
